function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';
import { TextInput, CheckBox, RadioButtonGroup, Text, Box } from 'grommet';
import { useFormikContext } from 'formik';
import { connect } from 'react-redux';
import Select from 'granite-admin/core/components/Select';
import MultiSelect from 'granite-admin/core/components/MultiSelect';
import FormField from 'granite-admin/core/components/FormField';
import CurrencyInput from 'granite-admin/core/components/CurrencyInput';
import NumberInput from 'granite-admin/core/components/NumberInput';
import AsyncSelect from 'granite-admin/core/components/AsyncSelect';
import * as MasterConfigDucks from 'granite-admin/common/ducks/masterConfig';
import { env } from 'granite-admin/env';
const defaultChangeModification = v => v;
const OptionsField = ({
  className,
  name,
  label,
  onChange,
  value,
  onBlur,
  error,
  updateMasterConfigData,
  optionsList,
  valueKey,
  labelKey,
  width,
  allowDecimals,
  allowNegativeValue,
  allowLeadingZeroes,
  disabled,
  checked,
  noBorder,
  normal,
  noFormField,
  disableSort,
  sortKey,
  fieldProps,
  showDescription,
  disableVisibility = false,
  onChangeModification = defaultChangeModification,
  required,
  formFieldProps
}) => {
  const {
    path: url,
    params
  } = useRouteMatch();
  const path = useMemo(() => {
    let theUrl = url;
    Object.keys(params).forEach(key => {
      if (key.includes('sr_')) theUrl = theUrl?.replace(`:${key}`, params[key]);
    });
    return theUrl;
  }, [url, params]);
  const {
    values,
    setFieldValue
  } = useFormikContext();
  const [options, setOptions] = useState(optionsList);
  const updateMasterConfig = updateMasterConfigData[path];
  //find the type of the field by its name
  const fieldType = updateMasterConfig.length && updateMasterConfig.find(i => i.key === name);
  const isFieldVisible = updateMasterConfig.length && updateMasterConfig.find(i => i.key === name && i.visibleOn);
  const isFieldHidden = updateMasterConfig.length && updateMasterConfig.find(i => i.key === name && i.hiddenOn);
  //find the field visibility rules ie the parent field on which the child field depends.
  const fieldVisibilty = updateMasterConfig.length && updateMasterConfig.find(i => {
    if (i.key === name && i.visibleOn) return i.config_master_light === false ? values[i.visibleOn]?.pk === i.selectedParentValue : values[i.visibleOn] === i.selectedParentValue;
    return false;
  });
  //find the parent field and its value on which this field is hidden
  const hiddenField = updateMasterConfig.length && updateMasterConfig.find(i => {
    if (i.key === name && i.hiddenOn) return i.config_master_light === false ? values[i.hiddenOn]?.pk === i.selectedParentValue : values[i.hiddenOn] === i.selectedParentValue;
    return false;
  });
  const compare = useCallback((a, b) => {
    return a[sortKey] < b[sortKey] ? -1 : a[sortKey] > b[sortKey] ? 1 : 0;
  }, [sortKey]);
  //optionsList sent to select atom mapped from possible values retreived from master-config options
  useEffect(() => {
    if (fieldVisibilty && fieldVisibilty.visibleValues && fieldVisibilty.visibleValues?.length && !optionsList.length) {
      const optionList = fieldVisibilty.visibleValues.map((i, index) => {
        let configMasterLight = fieldType?.config_master_light;
        let description = fieldVisibilty?.extra_data?.description?.[configMasterLight ? i : i.name];
        return configMasterLight === false ? {
          pk: i.pk,
          name: i.possible_value,
          sequence: index,
          value: i.possible_value,
          label: i.possible_value,
          description
        } : {
          pk: i,
          name: i,
          sequence: index,
          value: i,
          label: i,
          description
        };
      });
      if (!disableSort) optionList.sort(compare);
      setOptions(optionList);
    } else if (fieldType?.possible_values?.length && !optionsList.length) {
      const optionList = fieldType?.possible_values.map((i, index) => {
        let configMasterLight = fieldType?.config_master_light;
        let description = fieldType?.extra_data?.description?.[configMasterLight ? i : i.name];
        return configMasterLight === false ? {
          pk: i.pk,
          name: i.name,
          sequence: index,
          value: i.name,
          label: i.name,
          description
        } : {
          pk: i,
          name: i,
          sequence: index,
          value: i,
          label: i,
          description
        };
      });
      if (!disableSort) optionList.sort(compare);
      setOptions(optionList);
    } else {
      setOptions(optionsList);
    }
  }, [fieldType, optionsList, fieldVisibilty, disableSort, compare]);
  const visibleOn = !fieldType || fieldVisibilty && !hiddenField || !isFieldVisible && !isFieldHidden;
  const handleOnChange = async e => {
    switch (fieldType.value_type) {
      case 'TEXTCHOICE':
        await setFieldValue(name, onChangeModification(e.target.value));
        break;
      case 'BOOLEAN':
      case 'CHECKBOX':
        await setFieldValue(name, onChangeModification(e.target.checked));
        break;
      case 'INTEGER':
        let parsed = parseFloat(e.target.value);
        await setFieldValue(name, onChangeModification(allowLeadingZeroes ? e.target.value : isNaN(parsed) ? '' : parsed));
        break;
      case 'TEXTSELECT':
        await setFieldValue(name, onChangeModification(e.value));
        break;
      case 'MULTITEXTSELECT':
        await setFieldValue(name, onChangeModification(e.map(i => i.pk)));
        break;
      case 'API_BOUND_CONDITIONAL':
      case 'API_BOUND_SINGULAR':
        await setFieldValue(name, e);
        break;
      case 'TEXT':
      case 'CURRENCY':
      default:
        await setFieldValue(name, onChangeModification(e.target.value));
        break;
    }
    if (onChange && typeof onChange === 'function') {
      onChange(e, options);
    }
  };
  const renderConfigField = field => {
    if (field?.value_type === 'TEXT') {
      return /*#__PURE__*/React.createElement(TextInput, _extends({
        type: name,
        name: name,
        onChange: handleOnChange,
        onBlur: onBlur,
        value: value,
        disabled: disabled
      }, fieldProps));
    } else if (field?.value_type === 'INTEGER') {
      return /*#__PURE__*/React.createElement(NumberInput, _extends({
        name: name,
        value: value,
        onChange: handleOnChange,
        onBlur: onBlur,
        disabled: disabled,
        allowDecimals: allowDecimals,
        allowNegativeValue: allowNegativeValue,
        allowLeadingZeroes: allowLeadingZeroes
      }, fieldProps));
    } else if (field?.value_type === 'TEXTSELECT') {
      return /*#__PURE__*/React.createElement(Select, _extends({
        name: name,
        options: options,
        value: value,
        valueKey: valueKey,
        labelKey: labelKey,
        onBlur: onBlur,
        onChange: handleOnChange,
        disabled: disabled
      }, fieldProps));
    } else if (field?.value_type === 'MULTITEXTSELECT') {
      return /*#__PURE__*/React.createElement(MultiSelect, _extends({
        name: name,
        options: options,
        labelKey: labelKey,
        defaultSelectedOptions: options.filter(option => value?.includes(option?.pk)),
        onChange: handleOnChange,
        disabled: disabled,
        onBlur: onBlur
      }, fieldProps));
    } else if (field?.value_type === 'BOOLEAN') {
      return /*#__PURE__*/React.createElement(CheckBox, _extends({
        name: name,
        label: label,
        toggle: true,
        reverse: true,
        onChange: handleOnChange,
        checked: checked,
        disabled: disabled
      }, fieldProps));
    } else if (field?.value_type === 'TEXTCHOICE') {
      return /*#__PURE__*/React.createElement(RadioButtonGroup, _extends({
        name: name,
        options: options,
        value: value,
        onChange: handleOnChange,
        placeholder: label,
        disabled: disabled
      }, fieldProps));
    } else if (field?.value_type === 'CURRENCY') {
      return /*#__PURE__*/React.createElement(CurrencyInput
      // type="number"
      , _extends({
        precision: 0,
        name: name,
        value: value,
        onChange: handleOnChange,
        onBlur: onBlur,
        allowDecimals: allowDecimals,
        allowNegativeValue: allowNegativeValue,
        disabled: disabled
      }, fieldProps));
    } else if (field?.value_type === 'CHECKBOX') {
      return /*#__PURE__*/React.createElement(CheckBox, _extends({
        name: name,
        label: label,
        onChange: handleOnChange,
        defaultChecked: Boolean(checked) ?? false,
        disabled: disabled,
        onBlur: onBlur
      }, fieldProps));
    } else if ((field?.value_type === 'API_BOUND_CONDITIONAL' || field?.value_type === 'API_BOUND_SINGULAR') && options.length === 1) {
      const url = env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_BASE_URL + options[0].value;
      let label = labelKey;
      let apiBounds = field?.extra_data?.api_bounds;
      if (apiBounds) {
        apiBounds.forEach(i => {
          if (i.api_url === options[0].value) {
            label = i?.optionsLabelKey || labelKey;
          }
        });
      }
      return /*#__PURE__*/React.createElement(AsyncSelect, _extends({
        name: name,
        labelKey: label,
        valueKey: valueKey,
        defaultOptions: true,
        value: value,
        onBlur: onBlur,
        onChange: handleOnChange,
        dataSource: url,
        key: url
      }, fieldProps));
    } else {
      return /*#__PURE__*/React.createElement(TextInput, _extends({
        type: name,
        name: name,
        onChange: handleOnChange,
        onBlur: onBlur,
        value: value,
        disabled: disabled
      }, fieldProps));
    }
  };
  const renderOptionsField = field => {
    if (noFormField) {
      return renderConfigField(field);
    } else {
      let descriptionData = field?.extra_data?.description?.[value];
      if (descriptionData) descriptionData = descriptionData?.replaceAll('\n', '</>');
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormField, _extends({
        className: className,
        width: width,
        name: name,
        label: label,
        error: error,
        noBorder: noBorder,
        normal: normal,
        required: required
      }, formFieldProps), renderConfigField(field)), showDescription && descriptionData && /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Text, {
        weight: "600",
        color: "#868686"
      }, "Description:"), /*#__PURE__*/React.createElement(Text, {
        dangerouslySetInnerHTML: {
          __html: descriptionData
        }
      })));
    }
  };
  return visibleOn || disableVisibility ? renderOptionsField(fieldType) : null;
};
OptionsField.defaultProps = {
  optionsList: [],
  valueKey: {
    key: 'pk',
    reduce: true
  },
  labelKey: 'name',
  allowNegativeValue: false,
  allowDecimals: false,
  allowLeadingZeroes: false,
  noBorder: false,
  normal: false,
  noFormField: false,
  disableSort: true,
  sortKey: 'name',
  showDescription: false,
  required: false,
  formFieldProps: {}
};
OptionsField.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func,
  value: PropTypes.any,
  onBlur: PropTypes.func,
  error: PropTypes.any,
  updateMasterConfigData: PropTypes.any,
  optionsList: PropTypes.array,
  valueKey: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  labelKey: PropTypes.string,
  checked: PropTypes.any,
  noBorder: PropTypes.bool,
  normal: PropTypes.bool,
  noFormField: PropTypes.bool,
  fieldProps: PropTypes.object,
  disableSort: PropTypes.bool,
  sortKey: PropTypes.string,
  showDescription: PropTypes.bool,
  required: PropTypes.bool,
  formFieldProps: PropTypes.object
};
const mapStateToProps = state => ({
  updateMasterConfigData: MasterConfigDucks.updateMasterConfig(state)
});
export default connect(mapStateToProps)(OptionsField);